/** GxCroMemoList (screens §2 GxCroMemo): memo items grouped by severity, each with its text and projected-vs-limit line. Shared by solo review and "compare your view". */
import type { DraftView, TeamState } from "@bsim/contracts";
import { decisionPathLabel, type PreviewTrace } from "@bsim/domain";
import { formatKpi } from "../format";
import { valueBasisLine } from "../format/basis";
import { decisionIdOf } from "../decisions/decisionValues";
import { usePreviewRead } from "../shell/PreviewProvider";
import { kpiLabel, plainMemoText } from "../format/labels";
import { effectTableOf, type EffectTable } from "../decisions/effectTable";
import { EffectTableView } from "../decisions/EffectTableView";
import { GxBadge, type Tone } from "./primitives";
import "./cro-memo.css";

type Item = NonNullable<TeamState["croMemo"]>["memo"]["items"][number];

const GROUPS: readonly { readonly severity: Item["severity"]; readonly title: string; readonly tone: Tone; readonly mark: string }[] = [
  { severity: "objection", title: "Objections", tone: "crit", mark: "!" },
  { severity: "warn", title: "Warnings", tone: "warn", mark: "▲" },
  { severity: "info", title: "For your information", tone: "info", mark: "i" },
];

/** The projected-versus-limit line as a sentence. The rate shock is the one reading with a direction of its own: a drop in economic value, tested against a floor. */
const projectedText = (i: Item): string | null => {
  if (i.kpi === undefined || i.projected === undefined || i.limit === undefined) return null;
  const { kpi, projected, limit } = i;
  if (kpi === "eve200") {
    const move = `${projected < 0 ? "cut" : "raise"} equity value by ${formatKpi(kpi, Math.abs(projected))}`;
    return `Rate shock: the worse 2-point rate move is projected to ${move}, ${projected >= limit ? "inside" : "beyond"} your limit of ${formatKpi(kpi, Math.abs(limit))}.`;
  }
  return `${kpiLabel(kpi)}: projected ${formatKpi(kpi, projected)}, against your limit of ${formatKpi(kpi, limit)}.`;
};

/**
 * A decision the round open held back and the draft still holds (`DraftView.capped`) is already said on its row ("You wanted X; held at Y by Z"); the memo's note that a card's
 * hold ended last year would then read as if nothing held it, so it is left out for that decision.
 */
export const withoutEndedHolds = (items: readonly Item[], capped: DraftView["capped"]): readonly Item[] =>
  items.filter((i) => !(i.severity === "info" && (capped ?? []).some((c) => endedHoldNames(i.text, decisionPathLabel(c.decisionPath)))));

/** True when the note names `label` among the holds it says ended: the single form ("The card's hold on X ended with last year") or the grouped one ("Card holds on A, B, C ended with last year; …"). */
function endedHoldNames(text: string, label: string): boolean {
  if (text.includes(`'s hold on ${label} ended with last year`)) return true;
  const grouped = /Card holds on (.+?) ended with last year/.exec(text);
  return grouped !== null && (grouped[1] as string).split(", ").includes(label);
}

/** Sentences of a memo item: a full stop, a space and a capital start the next one ("$1,834.6M" and "2.4 percentage points" never split). */
const sentencesOf = (text: string): readonly string[] => text.split(/(?<=[.!?])\s+(?=[A-Z])/);
/** The detail under a verdict is this many sentences; the rest of an item sits in a fold. */
const DETAIL_SENTENCES = 2;

/**
 * The reconciling line for a memo note about one decision: the preview's own figure for it (three years, priced alone on the forecast path, what Review prints) beside the note's three-year whole-plan figure (the same score in the same dollars, differing by basis only); both name basis and horizon, and a one-year reading is never set beside them. Null when the note is about
 * several decisions or none, the preview has no figure for it, or the two print the same.
 */
export function memoReconcile(table: EffectTable, decisionIds: readonly string[] | undefined, trace: PreviewTrace | null | undefined): string | null {
  const usd = table.rows.find((r) => r.measure === "Projected score")?.usd;
  if (usd === undefined || decisionIds?.length !== 1) return null;
  const rows = trace?.decisions.filter((d) => decisionIdOf(d.decisionId) === decisionIds[0]) ?? [];
  if (rows.length === 0 || rows.some((d) => d.valueUsdPerShare === undefined)) return null;
  const inPlan = Number(usd.replace("\u2212", "-").replace("$", ""));
  return Number.isNaN(inPlan) ? null : valueBasisLine({ alone: rows.reduce((t, d) => t + (d.valueUsdPerShare as number), 0), inPlan });
}

/** The memo's single item on all the changed decisions together (it is not a family line). */
const TOGETHER = /^Together your decisions move your projected score/;

/** One item as a player reads it: a one-line verdict (the measure and where it stands), a short detail paragraph, and the rest folded under "More". The words are the engine's, in order, none dropped. */
function MemoText({ text, decisionIds }: { readonly text: string; readonly decisionIds?: readonly string[] | undefined }) {
  const trace = usePreviewRead()?.trace;
  // A note that says what one decision moves reads as the same small table the Decide sheet shows: measure, change, year-end.
  const table = effectTableOf(text) ?? effectTableOf(plainMemoText(text));
  // The note's one sentence on the decisions together (lines above + the way they work together = the total): read whole, as a sentence, not as a family line.
  if (table === null && TOGETHER.test(text)) return <div className="gx-narr gx-memo-text"><p className="gx-memo-together" data-testid="memo.together">{plainMemoText(text)}</p></div>;
  if (table !== null) return <div className="gx-narr gx-memo-text" data-testid="memo.effect"><p className="gx-memo-verdict" data-testid="memo.verdict">{/^(Your .+?) moves?/.exec(text)?.[1] ?? "This decision"} moves</p><EffectTableView table={table} testId="memo.effect.table" reconcile={memoReconcile(table, decisionIds, trace)} /></div>;
  const [verdict = "", ...rest] = sentencesOf(plainMemoText(text));
  const detail = rest.slice(0, DETAIL_SENTENCES).join(" "), more = rest.slice(DETAIL_SENTENCES).join(" ");
  return (
    <div className="gx-narr gx-memo-text">
      <p className="gx-memo-verdict" data-testid="memo.verdict">{verdict}</p>
      {detail === "" ? null : <p className="gx-memo-detail">{detail}</p>}
      {more === "" ? null : <details className="gx-memo-fold"><summary>More</summary><p className="gx-memo-detail">{more}</p></details>}
    </div>
  );
}

/** The note's header line, counted from the sections the note lists (its own headings, the ended-hold notes it leaves out left out here too): "2 objections, 4 warnings, 3 for your information". Null when the note lists nothing. */
export function memoReceipt(all: readonly Item[], capped?: DraftView["capped"]): string | null {
  const items = withoutEndedHolds(all, capped);
  if (items.length === 0) return null;
  return GROUPS.flatMap((g) => { const n = items.filter((i) => i.severity === g.severity).length; return n === 0 ? [] : [`${n} ${n === 1 ? g.title.toLowerCase().replace(/s$/, "") : g.title.toLowerCase()}`]; }).join(", ");
}

export function GxCroMemoList({ items: all, testPrefix, capped }: { readonly items: readonly Item[]; readonly testPrefix: string; readonly capped?: DraftView["capped"] }) {
  const items = withoutEndedHolds(all, capped);
  return (
    <>
      {GROUPS.map((g) => {
        const group = items.filter((i) => i.severity === g.severity);
        return group.length === 0 ? null : (
          <div key={g.severity} className="gx-memo-group" data-testid={`${testPrefix}.${g.severity}`}>
            <div className="gx-eyebrow">{g.title}</div>
            {group.map((i) => (
              <div key={i.text} className="gx-memo-item" data-severity={g.severity}>
                <GxBadge tone={g.tone}>{g.mark}</GxBadge>
                <div><MemoText text={i.text} decisionIds={i.decisionIds} />{projectedText(i) === null ? null : <div className="gx-small gx-faint gx-num">{projectedText(i)}</div>}</div>
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}
